import {selectIsCodeComponentCanvas} from '@packages/systems/component-canvas/universal';
import {selectComponentCanvasComponentId} from '@packages/systems/component-canvas/designer';
import {selectDesignerAbility} from '@packages/systems/designer-abilities';
import {useFirstTimeUse} from '@packages/systems/designer/apps/client';
import {selectIsEnterpriseBillingExperienceCTAEnabled} from '@packages/systems/billing/client';
import {selectSiteShortName} from '@packages/systems/designer-selectors';
import {useTranslation} from '@packages/systems/designer-translations';
import {
  type DesignerStores,
  useDesignerSelector,
} from '@packages/systems/designer/flux';
import {
  selectSiteId,
  selectWorkspaceId,
  selectWorkspaceSlug,
} from '@packages/systems/designer/site-data';
import {webflowDefaultHost} from '@packages/utilities/environment';
import {buildAccountTeamHref} from '@packages/utilities/support-links';
import {QUERY_PARAMS} from '@packages/systems/core/constants/analytics';
import type {ImageUploadResult} from '@packages/utilities/image-upload/shared';
import type {PasteAttachment} from '@packages/domains/ai-assistant/data-types';
// Deep imports (not the assistant-next barrel) so we only pull the needed
// selectors/helpers, not AssistantChat and its ESM-only deps.
// eslint-disable-next-line webflow/module-top-level-imports
import {
  selectAiaSelectedCmsCollections,
  selectCodeStreamingState,
} from '@packages/systems/ai/assistant-next/frontend/selectors';
// eslint-disable-next-line webflow/module-top-level-imports
import {cmsCollectionsToContextTags} from '@packages/systems/ai/assistant-next/frontend/hooks/useCmsPickerBinding';
import {isAiccShortCreatePrompt} from '@packages/systems/ai/assistant-next/shared';
import {useFeatureFlag} from '@packages/systems/feature-config/client';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import React, {useCallback, useEffect} from 'react';
import {aiccClarificationAnalytics} from './aiccClarificationAnalytics';
import {CodeComponentCreator} from './CodeComponentCreator';
import {CodeComponentClarification} from './CodeComponentClarification';
import {CodeEditorFloatingPane} from './CodeEditorFloatingPane';
import {
  aiAssistantOpened,
  codeComponentClarificationStarted,
  codeComponentCreatorDismissed,
  visualEditingOnboardingGenerationStarted,
} from './flux/actionCreators';
import {
  selectShowCodeComponentClarification,
  selectShowCodeComponentCreator,
  selectShowVisualEditingOnboarding,
  useCodeComponentDispatch,
  useCodeComponentSelector,
} from './flux/CodeLibrariesSlice';
import {
  AICC_USED_NOTE_ID,
  selectEditableCodeComponentIds,
  shouldStartStandaloneVisualEditingOnboarding,
  VISUAL_EDITING_ONBOARDING_NOTE_ID,
} from './VisualEditingOnboardingPopover';
import {useCaptureCodeComponentScreenshot} from './useCaptureCodeComponentScreenshot';

const canEditCodeComponentsSelector = selectDesignerAbility(
  'canEditCodeComponents'
);

const selectUserId = (state: DesignerStores): string =>
  state.SiteDataStore.userId;

type CodeComponentAppProps = {
  shouldRouteEnterpriseToPlans: boolean;
  workspaceLeftInset?: number;
  workspaceRightInset?: number;
};

function CodeComponentAppInner({
  shouldRouteEnterpriseToPlans,
  workspaceLeftInset,
  workspaceRightInset,
}: CodeComponentAppProps) {
  const t = useTranslation();
  const dispatch = useCodeComponentDispatch();
  const isCodeComponentCanvas = useCodeComponentSelector(
    selectIsCodeComponentCanvas
  );
  const showCreator = useCodeComponentSelector(selectShowCodeComponentCreator);
  const showClarification = useCodeComponentSelector(
    selectShowCodeComponentClarification
  );
  const showVisualEditingOnboarding = useCodeComponentSelector(
    selectShowVisualEditingOnboarding
  );
  const siteId = useDesignerSelector(selectSiteId);
  const workspaceId = useDesignerSelector(selectWorkspaceId);
  const userId = useDesignerSelector(selectUserId);
  const componentCanvasComponentId = useDesignerSelector(
    selectComponentCanvasComponentId
  );

  // Enrich clarification funnel events with site context (raw trackEvent has
  // no useTrackEvent middleware). Keep this ahead of any start()/answer path.
  useEffect(() => {
    aiccClarificationAnalytics.setSiteContext({siteId, workspaceId, userId});
  }, [siteId, workspaceId, userId]);
  const streamState = useCodeComponentSelector(selectCodeStreamingState);
  const isStreamingForThisComponent =
    streamState.isStreaming &&
    streamState.componentId === componentCanvasComponentId;
  useCaptureCodeComponentScreenshot({
    componentId: componentCanvasComponentId,
    isStreamingForThisComponent,
  });
  const editableCodeComponentIds = useDesignerSelector(
    selectEditableCodeComponentIds
  );
  const [isVisualEditingOnboardingFirstTimeUse] = useFirstTimeUse(
    VISUAL_EDITING_ONBOARDING_NOTE_ID
  );
  const [isAiccUsageFirstTimeUse, markAiccUsed] =
    useFirstTimeUse(AICC_USED_NOTE_ID);
  const canEditCodeComponents = useDesignerSelector(
    canEditCodeComponentsSelector
  );
  const canUseCodeComponents = useDesignerSelector(
    selectDesignerAbility('canUseCodeComponents')
  );
  const siteShortName = useDesignerSelector(selectSiteShortName);
  const cmsPickerEnabled = useFeatureFlag(FEATURE_FLAGS.AICC_CMS_PICKER);
  const clarificationEnabled = useFeatureFlag(FEATURE_FLAGS.AICC_CLARIFICATION);
  const isVisualEditingEnabled = useFeatureFlag(
    FEATURE_FLAGS.VIBE_COMPONENT_ELEMENT_SELECTION
  );
  const selectedCmsCollections = useDesignerSelector(
    selectAiaSelectedCmsCollections
  );
  const shouldStartVisualEditingOnboarding =
    shouldStartStandaloneVisualEditingOnboarding({
      canEditCodeComponents,
      componentCanvasComponentId,
      editableCodeComponentIds,
      isOnboardingFirstTimeUse: isVisualEditingOnboardingFirstTimeUse,
      isVisualEditingEnabled,
      showVisualEditingOnboarding,
    });
  const isSelfServeBillingBlocked = useDesignerSelector(
    selectIsEnterpriseBillingExperienceCTAEnabled
  );
  const accountTeamWorkspaceSlug = useDesignerSelector(selectWorkspaceSlug);
  const accountTeamHref = buildAccountTeamHref(
    'webflow_designer',
    'code-components-app',
    accountTeamWorkspaceSlug
  );
  const shouldUseAccountTeam =
    isSelfServeBillingBlocked && !shouldRouteEnterpriseToPlans;
  const upgradeUrl = shouldUseAccountTeam
    ? accountTeamHref
    : siteShortName
      ? `${webflowDefaultHost}/dashboard/sites/${siteShortName}/plans?ref=${QUERY_PARAMS.REF.DESIGNER_AICC_UPGRADE}`
      : '';

  const handleSubmit = useCallback(
    (
      prompt: string = '',
      images?: ImageUploadResult[],
      fromSuggestion?: boolean,
      pasteAttachments?: PasteAttachment[]
    ) => {
      // Snapshot the picked collections onto the creator's first message so it
      // renders their chips, matching the ongoing-edit composer.
      const contextTags = cmsPickerEnabled
        ? cmsCollectionsToContextTags(selectedCmsCollections)
        : [];
      // Short prompts enter the fullscreen clarification shell; long prompts
      // bypass it and go straight to the AIA sidebar for generation.
      // Start clarification before dismissing the creator so the fullscreen
      // overlay never drops between the two actions (sidebar stays suppressed
      // and CodeComponentApp can swap Creator → Clarification in one turn).
      // Arm onboarding before dismiss so first-time create + clarification
      // keeps clarificationUserPrompt for the PromptBox.
      const needsClarification =
        clarificationEnabled && isAiccShortCreatePrompt(prompt);
      if (needsClarification) {
        aiccClarificationAnalytics.setSiteContext({
          siteId,
          workspaceId,
          userId,
        });
        aiccClarificationAnalytics.start({
          promptText: prompt,
          componentId: componentCanvasComponentId,
          isRetry: false,
        });
        dispatch(codeComponentClarificationStarted());
      }
      if (shouldStartVisualEditingOnboarding) {
        dispatch(visualEditingOnboardingGenerationStarted());
      }
      dispatch(
        codeComponentCreatorDismissed({
          text: prompt,
          images,
          fromSuggestion,
          fromCreator: true,
          ...(contextTags.length > 0 && {contextTags}),
          ...(pasteAttachments?.length && {pasteAttachments}),
        })
      );
      if (isAiccUsageFirstTimeUse) {
        markAiccUsed();
      }
      dispatch(aiAssistantOpened({clearConversation: true}));
    },
    [
      dispatch,
      cmsPickerEnabled,
      selectedCmsCollections,
      shouldStartVisualEditingOnboarding,
      clarificationEnabled,
      isAiccUsageFirstTimeUse,
      markAiccUsed,
      componentCanvasComponentId,
      siteId,
      workspaceId,
      userId,
    ]
  );

  if (!isCodeComponentCanvas) {
    return null;
  }

  if (showCreator) {
    return (
      <CodeComponentCreator
        onSubmit={handleSubmit}
        siteShortName={siteShortName}
        showUpgradeBanner={!canUseCodeComponents}
        upgradeUrl={upgradeUrl}
        upgradeCtaLabel={
          shouldUseAccountTeam
            ? t('designer.enterprise-billing.contact-account-team-action')
            : shouldRouteEnterpriseToPlans
              ? 'Add a Site plan'
              : undefined
        }
        upgradeBody={
          shouldUseAccountTeam
            ? t('designer.enterprise-billing.code-component.publish-body')
            : shouldRouteEnterpriseToPlans
              ? 'Add a Site plan to access code components.'
              : undefined
        }
      />
    );
  }

  if (showClarification) {
    return <CodeComponentClarification />;
  }

  return (
    <CodeEditorFloatingPane
      workspaceLeftInset={workspaceLeftInset}
      workspaceRightInset={workspaceRightInset}
    />
  );
}

export function CodeComponentApp(props: CodeComponentAppProps) {
  return <CodeComponentAppInner {...props} />;
}
